import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import Header from "@components/shared/header";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";
import { useRouter } from "next/router";
import colors from "@styles/variables/camplejeuneapplicationVariables.module.scss";
import BaseCamplejeuneLogo from "@components/basecamplejeune/logo";

const Modal = dynamic(() => import("@components/shared/modal"));
const Footer = dynamic(() => import("@components/shared/footer"));
const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

export default function CampLejeuneApplicationWrapper({
    children,
    footerBgColor,
}: {
    children: ReactNode;
    footerBgColor?: string;
}): ReactElement {
    const { modal, closeModal } = useModal();
    const { query } = useRouter();
    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return (
                    <PrivacyPolicy
                        category="Camp Lejeune"
                        domain="BaseCampLejeune.com"
                    />
                );
            case "terms":
                return (
                    <TermsAndConditions
                        category="Camp Lejeune"
                        domain="BaseCampLejeune.com"
                    />
                );
            case "sideWidget":
                return <Mobile />;

            default:
                return null;
        }
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700&display=swap"
                    rel="stylesheet"
                />
            </Head>

            <div style={{ fontFamily: `'Poppins', sans-serif` }}>
                <Header
                    slogan="Get Maximum Justice for Your Camp Lejeune Injuries"
                    textColor={colors.offerCallNumber}
                    stickyStyle={{
                        stickTitleColor: colors.stickTitleColor,
                        stickBtnTxtColor: colors.stickBtnTxtColor,
                        stickBtnBgColor: colors.stickBtnBgColor,
                        stickBtnBorderColor: colors.stickBtnBorderColor,
                    }}
                >
                    <BaseCamplejeuneLogo />
                </Header>
                <main
                    className={`max-w-7xl mx-auto block px-4vw lg:px-4 ${
                        query.ts ? "withTS" : "withoutTS"
                    }`}
                >
                    {children}
                </main>
                <Footer bgColor={footerBgColor} />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={
                        modalContent === "privacy"
                            ? "Privacy Policy"
                            : modalContent === "terms"
                            ? "Terms and Conditions"
                            : modalContent === "sideWidget"
                            ? "Contents"
                            : undefined
                    }
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
